import React, { useState, useRef, useEffect, useCallback } from "react";
import { Board, BoardPermission } from "../../../types";
import ListIcon from "app/components/Icons/ListIcon";
import KabanIcon from "app/components/Icons/KabanIcon";
import CustomTooltip from "app/components/Tooltip";
import UserGroupIcon from "app/components/Icons/UserGroupIcon";
import PasswordIcon from "app/components/Icons/PasswordIcon";
import { CogIcon } from "app/components/Icons";
import IconText from "app/components/IconText";
import EditButton from "../../../components/EditButton";
import CopyButton from "../../../components/CopyButton";
import FavoriteButton from "../../../components/FavoriteButton";
import Input from "app/components/Input";
import { useDispatch } from "react-redux";
import { actions } from "../../../redux/slice";
import { BoardViewType } from "app/types";

interface EditableRowItemProps {
  isEditing: boolean;
  board: Board;
  setEditingBoardId: (boardId: string | null) => void;
  handleBoardRename: (board: Board) => void;
  handleCopyBoardLink: () => void;
  handleFavoriteBoard: () => void;
}

const getBoardIcon = (type: BoardViewType) => {
  return type === BoardViewType.LIST ? (
    <ListIcon color="#79808A" />
  ) : (
    <KabanIcon color="#79808A" />
  );
};

const getPermissionIcon = (permission: BoardPermission) => {
  if (permission === BoardPermission.PUBLIC) {
    return (
      <CustomTooltip text="Public permission">
        <UserGroupIcon color="#79808A" />
      </CustomTooltip>
    );
  }
  if (permission === BoardPermission.PRIVATE) {
    return (
      <CustomTooltip text="Private permission">
        <PasswordIcon color="#79808A" size="20" />
      </CustomTooltip>
    );
  }
  return (
    <CustomTooltip text="Custom permission">
      <CogIcon color="#79808A" size={20} />
    </CustomTooltip>
  );
};

export default function EditableRowItem({
  board,
  handleBoardRename,
  isEditing,
  setEditingBoardId,
  handleCopyBoardLink,
  handleFavoriteBoard,
}: EditableRowItemProps) {
  const dispatch = useDispatch();
  const spanRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [boardName, setBoardName] = useState(board?.name || "");

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      inputRef.current.style.width = `${spanRef.current.offsetWidth + 10}px`; // Adjust width dynamically
    }
  }, [boardName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setEditingBoardId(null); // Close edit mode when clicking outside
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, setEditingBoardId]);

  const handleRename = useCallback(() => {
    if (boardName.trim()) {
      setEditingBoardId(null);
      dispatch(
        actions.renameBoardName({
          id: board?._id,
          name: boardName.trim(),
        }),
      );
    }
  }, [boardName, board, dispatch, setEditingBoardId]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleRename();
      }
    },
    [handleRename],
  );
  return (
    <>
      {/* Hidden span to measure text width */}
      <span ref={spanRef} className="absolute invisible whitespace-pre">
        {boardName || "Placeholder"}
      </span>

      {isEditing ? (
        <>
          <Input
            id="board-name"
            name="boardName"
            inputRef={inputRef}
            value={boardName}
            onChange={(e) => setBoardName(e.target.value)}
            icon={getBoardIcon(board?.viewType)}
            inputClassName="!rounded-none !p-2 h-full min-h-[40px] text-sm leading-[21px] font-normal text-[#333E4D]"
            style={{ width: "100%", maxWidth: "425px", minWidth: "50px" }}
            onKeyDown={handleInputKeyDown}
            endIcon={
              <div className="flex items-center justify-center">
                {board && getPermissionIcon(board.permission)}
              </div>
            }
          />
        </>
      ) : (
        <>
          <span className="flex items-center gap-2 max-w-[225px] overflow-hidden">
            <IconText
              text={""}
              icon={getBoardIcon(board?.viewType || BoardViewType.LIST)}
              iconClassName="size-[20px]"
              className="h-full min-h-[40px] gap-[6px]"
              isTruncate={true}
              textClassName="text-sm leading-[21px] font-normal text-[#333E4D]"
            />
            <span className="truncate text-sm leading-[21px] font-normal text-[#333E4D]">
              {board?.name}
            </span>
          </span>
          <div className="flex items-center justify-center">
            {board && getPermissionIcon(board.permission)}
          </div>
        </>
      )}

      {!isEditing && (
        <>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <EditButton
              title="Rename"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (board) {
                  handleBoardRename(board);
                }
              }}
            />
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <CopyButton
              copyText={board?.name || ""}
              onClick={handleCopyBoardLink}
              className="bg-white border border-[#E9ECF2] hover:bg-[#ECEEF2] rounded-[2px]"
            />
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <FavoriteButton
              isFavorite={board?.favorite == 0 ? false : true}
              onClick={handleFavoriteBoard}
            />
          </div>
        </>
      )}
    </>
  );
}
